import { useState } from "react";
import { toast } from "sonner";
import { Check, X, BellRing, Plus, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { useFetch } from "../../lib/useFetch";
import { api, errMsg, eur, fileUrl, fmtDate, todayIso } from "../../lib/api";
import { StatusBadge } from "../../components/site/FileUpload";
import { FormGrid } from "./FormField";
import { AdminH } from "./AdminLayout";

const PAY_FIELDS = (accounts) => [
  { key: "account", label: "Account", type: "select", required: true, options: { static: accounts } },
  { key: "kind", label: "Type", type: "select", required: true, options: { static: [["deposit", "Deposit"], ["balance", "Balance"], ["payment", "Payment"], ["refund", "Refund"]] } },
  { key: "amount", label: "Amount €", type: "number" }, { key: "date", label: "Date", type: "date" },
  { key: "method", label: "Method", type: "select", required: true, options: { static: ["Bank transfer", "PayPal", "Cash", "Card (POS)"] } }, { key: "reason", label: "Reason (causale)" },
  { key: "receipt_path", label: "Receipt (upload)", type: "image", full: true }, { key: "note", label: "Note", type: "textarea", full: true },
];

export const LedgerDialog = ({ r, onClose }) => {
  const [row, setRow] = useState(r);
  const [f, setF] = useState({ account: r.accounts[0]?.account || "clinic", kind: "payment", date: todayIso(), method: "Bank transfer" });
  const record = async () => {
    try {
      const { data } = await api.post("/admin/payments", { ...f, registration_id: r.id });
      setRow(data);
      toast.success("Payment recorded");
      setF({ ...f, amount: null, reason: "", note: "", receipt_path: "" });
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const delPayment = async (p) => {
    if (!window.confirm(`Delete this ${p.kind} of ${eur(p.amount)}?`)) return;
    try {
      const { data } = await api.delete(`/admin/payments/${p.id}`);
      setRow(data);
      toast.success("Payment deleted");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto" data-testid="ledger-dialog">
        <DialogHeader><DialogTitle>{row.sailor_name} · {row.event_title}</DialogTitle></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-3">
          {row.accounts.map((a) => (
            <div key={a.account} className="rounded-xl border border-slate-200 p-4" data-testid={`ledger-account-${a.account}`}>
              <p className="text-xs font-bold uppercase text-slate-500">{a.account}</p><p className="font-display text-xl font-bold">{eur(a.total)}</p>
              <p className="text-xs text-slate-500">Paid {eur(a.paid - a.refunded)} · Due {eur(a.balance)}</p><StatusBadge status={a.status} />
            </div>
          ))}
        </div>
        <div>
          <p className="mb-2 text-sm font-bold text-[#0A2540]">Operations history</p>
          <table className="w-full text-sm" data-testid="ledger-history">
            <tbody className="divide-y divide-slate-100">
              {row.payments.map((p) => (
                <tr key={p.id}><td className="py-2">{fmtDate(p.date)}</td><td>{p.account}</td><td>{p.kind}</td><td>{p.method}</td><td>{p.reason}</td>
                  <td className={`text-right font-semibold ${p.kind === "refund" ? "text-rose-600" : ""}`}>{p.kind === "refund" ? "-" : ""}{eur(p.amount)}</td>
                  <td className="pl-2">{p.receipt_path && <a href={fileUrl(p.receipt_path)} target="_blank" rel="noreferrer" className="text-xs underline">receipt</a>}</td><td className="pl-2 text-xs text-slate-400">{p.created_by}</td>
                  <td className="pl-2 text-right"><button onClick={() => delPayment(p)} className="text-slate-400 hover:text-rose-600" title="Delete payment" data-testid={`ledger-delete-${p.id}`}><Trash2 size={14} /></button></td></tr>
              ))}
              {!row.payments.length && <tr><td className="py-2 text-slate-500">No operations yet.</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="rounded-2xl bg-slate-50 p-4">
          <p className="mb-3 flex items-center gap-2 text-sm font-bold text-[#0A2540]"><Plus size={14} />Record payment</p>
          <FormGrid fields={PAY_FIELDS(row.accounts.map((a) => a.account))} f={f} setF={setF} />
          <button onClick={record} className="btn-buoy mt-4 !py-2" data-testid="ledger-record-payment">Record</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Receipts = () => {
  const { data, reload } = useFetch("/admin/receipts?status=pending");
  const [pending, setPending] = useState(null);
  const verify = async (r, approve, kind = "payment") => {
    try {
      await api.post(`/admin/receipts/${r.id}/verify`, { approve, kind });
      toast.success(approve ? "Receipt verified & payment recorded" : "Receipt rejected");
      setPending(null);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="space-y-3">
      {data && !data.length && <p className="text-slate-500">No receipts to verify.</p>}
      {(data || []).map((r) => (
        <div key={r.id} className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-white p-5" data-testid={`receipt-row-${r.id}`}>
          <div><p className="font-semibold text-[#0A2540]">{r.user?.first_name} {r.user?.last_name} · {r.event_title}</p>
            <p className="text-sm text-slate-500">{r.account} · {eur(r.amount)} · {fmtDate(r.created_at)} · <a href={fileUrl(r.path)} target="_blank" rel="noreferrer" className="underline" data-testid={`receipt-view-${r.id}`}>{r.filename || "view file"}</a></p></div>
          <div className="flex gap-2">
            <button onClick={() => setPending({ r, kind: "payment" })} className="inline-flex items-center gap-1 rounded-full bg-emerald-600 px-4 py-2 text-sm font-semibold text-white" data-testid={`receipt-approve-${r.id}`}><Check size={14} />Verify</button>
            <button onClick={() => verify(r, false)} className="inline-flex items-center gap-1 rounded-full border border-rose-300 px-4 py-2 text-sm font-semibold text-rose-600" data-testid={`receipt-reject-${r.id}`}><X size={14} />Reject</button>
          </div>
        </div>
      ))}
      <Dialog open={!!pending} onOpenChange={(o) => !o && setPending(null)}>
        <DialogContent className="max-w-sm" data-testid="verify-dialog">
          <DialogHeader><DialogTitle>Verify receipt</DialogTitle></DialogHeader>
          {pending && <>
            <p className="text-sm text-slate-500">{pending.r.user?.first_name} {pending.r.user?.last_name} · {pending.r.account} · {eur(pending.r.amount)}</p>
            <select value={pending.kind} onChange={(e) => setPending({ ...pending, kind: e.target.value })} className="h-10 rounded-md border border-slate-200 px-3 text-sm" data-testid="verify-kind-select">
              <option value="deposit">Deposit</option><option value="balance">Balance</option><option value="payment">Payment</option>
            </select>
            <button onClick={() => verify(pending.r, true, pending.kind)} className="btn-buoy w-fit" data-testid="verify-confirm">Verify & record payment</button>
          </>}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const EventFinancials = () => {
  const { data: events } = useFetch("/admin/events");
  const [eid, setEid] = useState("");
  const { data, reload } = useFetch(eid ? `/admin/events/${eid}/financials` : null);
  const [ledger, setLedger] = useState(null);
  const remind = async (r) => {
    try { await api.post(`/admin/registrations/${r.id}/remind`); toast.success("Payment reminder sent (simulated email)"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-6">
      <select value={eid} onChange={(e) => setEid(e.target.value)} className="h-11 w-full max-w-md rounded-xl border border-slate-200 bg-white px-3 text-sm" data-testid="financials-event-select">
        <option value="">Select an event…</option>{(events || []).map((e) => <option key={e.id} value={e.id}>{e.title} ({fmtDate(e.start_date)})</option>)}
      </select>
      {data && eid && (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-2xl bg-[#0A2540] p-5 text-white"><p className="text-xs uppercase text-white/60">Expected</p><p className="font-display text-2xl font-bold" data-testid="fin-expected">{eur(data.expected)}</p></div>
            <div className="rounded-2xl bg-emerald-600 p-5 text-white"><p className="text-xs uppercase text-white/70">Collected</p><p className="font-display text-2xl font-bold" data-testid="fin-collected">{eur(data.collected)}</p></div>
            {data.accounts.map((a) => <div key={a.account} className="rounded-2xl border border-slate-200 bg-white p-5"><p className="text-xs uppercase text-slate-500">{a.account}</p><p className="font-display text-xl font-bold">{eur(a.paid - a.refunded)} / {eur(a.total)}</p></div>)}
          </div>
          <div className="flex flex-wrap gap-2">{Object.entries(data.by_status).map(([s, n]) => <StatusBadge key={s} status={s} label={`${s}: ${n}`} />)}</div>
          <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500"><tr>{["Sailor", "Clinic", "Charter", "Services", "Total", "Paid", "Balance", "Status", ""].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}</tr></thead>
              <tbody className="divide-y divide-slate-100">
                {data.rows.map((r) => {
                  const acc = (k) => r.accounts.find((a) => a.account === k);
                  return (
                    <tr key={r.id} data-testid={`fin-row-${r.id}`}>
                      <td className="px-4 py-3 font-semibold">{r.sailor_name}{r.missing_payment_flag && <BellRing size={12} className="ml-1 inline text-[#FF5722]" />}</td>
                      {["clinic", "charter", "services"].map((k) => <td key={k} className="px-4 py-3">{acc(k) ? <>{eur(acc(k).total)}<span className="block text-[10px] text-slate-500">{acc(k).status}</span></> : "—"}</td>)}
                      <td className="px-4 py-3">{eur(r.total)}</td><td className="px-4 py-3">{eur(r.paid - r.refunded)}</td><td className="px-4 py-3 font-semibold">{eur(r.balance)}</td>
                      <td className="px-4 py-3"><StatusBadge status={r.payment_status} /></td>
                      <td className="whitespace-nowrap px-4 py-3 text-right">
                        <button onClick={() => setLedger(r)} className="mr-2 rounded-full bg-[#0A2540] px-3 py-1.5 text-xs font-semibold text-white" data-testid={`fin-ledger-${r.id}`}>Ledger</button>
                        {r.balance > 0 && <button onClick={() => remind(r)} className="rounded-full border border-[#FF5722] px-3 py-1.5 text-xs font-semibold text-[#FF5722]" data-testid={`fin-remind-${r.id}`}>Flag & remind</button>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
      {ledger && <LedgerDialog r={ledger} onClose={() => { setLedger(null); reload(); }} />}
    </div>
  );
};

export default function AdminPayments() {
  return (
    <div data-testid="admin-payments">
      <AdminH title="Payments" sub="Manual registration, receipt verification and event accounting. Online payments with PayPal are recorded automatically when PayPal is active in Site settings." />
      <Tabs defaultValue="receipts">
        <TabsList><TabsTrigger value="receipts" data-testid="payments-tab-receipts">Receipts to verify</TabsTrigger><TabsTrigger value="events" data-testid="payments-tab-events">Event accounting</TabsTrigger></TabsList>
        <TabsContent value="receipts" className="mt-6"><Receipts /></TabsContent>
        <TabsContent value="events" className="mt-6"><EventFinancials /></TabsContent>
      </Tabs>
    </div>
  );
}
